import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronsUpDown,
  Crown,
  Settings,
  HelpCircle,
  LogOut,
  Sparkles,
  UserRound,
} from "lucide-react";
import useAuthStore from "@/store/use-auth-store";
import { cn, getFileUrl } from "@/lib/utils";
import { useLeftSidebar } from "../ui/sidebar";
import { isMobile } from "@/lib/is-mobile";
import useModalStore from "@/store/use-modal-store";
import { usePricingActions } from "@/features/pricing/hooks/use-pricing-actions";
import { useBillingHistory } from "@/api/hooks/use-query-api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarTooltip } from "./SidebarTooltip";
import mixpanel from "mixpanel-browser";
import { registerClientSource } from "@/lib/client-source";
import { isModifiedClick } from "@/lib/link-utils";

const FancyUpgradeButton = ({
  onClick,
  label,
}: {
  onClick: () => void;
  label: string;
}) => (
  <motion.button
    onClick={onClick}
    className="relative w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold text-white rounded-xl overflow-hidden cursor-pointer focus:outline-none"
    style={{
      background:
        "linear-gradient(135deg, #7c3aed 0%, #a855f7 45%, #6366f1 100%)",
      boxShadow:
        "0 0 18px rgba(124, 58, 237, 0.45), 0 2px 8px rgba(0,0,0,0.25)",
    }}
    whileHover={{
      scale: 1.02,
      boxShadow:
        "0 0 28px rgba(168, 85, 247, 0.65), 0 4px 14px rgba(0,0,0,0.35)",
    }}
    whileTap={{ scale: 0.97 }}
  >
    {/* Shimmer sweep */}
    <motion.div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%)",
        skewX: "-15deg",
      }}
      animate={{ x: ["-160%", "160%"] }}
      transition={{
        duration: 1.8,
        repeat: Infinity,
        repeatDelay: 3.5,
        ease: "easeInOut",
      }}
    />
    {/* Top highlight line */}
    <div
      className="absolute inset-x-0 top-0 h-px pointer-events-none"
      style={{
        background:
          "linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)",
      }}
    />
    {/* Pulsing glow ring */}
    <motion.div
      className="absolute inset-0 rounded-xl pointer-events-none"
      animate={{ opacity: [0.4, 0.8, 0.4] }}
      transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
      style={{ boxShadow: "inset 0 0 12px rgba(255,255,255,0.08)" }}
    />
    {/* Crown icon with wobble */}
    <motion.div
      animate={{ rotate: [0, -8, 8, -4, 4, 0] }}
      transition={{ duration: 3.5, repeat: Infinity, repeatDelay: 2.5 }}
      className="relative z-10"
    >
      <Crown className="w-4 h-4 drop-shadow-sm" />
    </motion.div>
    <span className="relative z-10 tracking-wide">{label}</span>
    {/* Sparkle particles */}
    <motion.div
      className="absolute top-1.5 right-3 w-1 h-1 bg-white rounded-full pointer-events-none"
      animate={{ opacity: [0, 1, 0], scale: [0, 1.2, 0] }}
      transition={{
        duration: 1.2,
        repeat: Infinity,
        repeatDelay: 3,
        delay: 0.8,
      }}
    />
    <motion.div
      className="absolute bottom-1.5 right-5 w-0.5 h-0.5 bg-white/80 rounded-full pointer-events-none"
      animate={{ opacity: [0, 1, 0], scale: [0, 1.2, 0] }}
      transition={{
        duration: 1.2,
        repeat: Infinity,
        repeatDelay: 3,
        delay: 1.6,
      }}
    />
    <motion.div
      className="absolute top-2 left-6 w-0.5 h-0.5 bg-purple-200 rounded-full pointer-events-none"
      animate={{ opacity: [0, 1, 0], scale: [0, 1.2, 0] }}
      transition={{
        duration: 1.2,
        repeat: Infinity,
        repeatDelay: 3,
        delay: 0.3,
      }}
    />
  </motion.button>
);

export default function Profile() {
  const { open: isSidebarOpen, setOpen: setSidebarOpen } = useLeftSidebar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { openLogout } = useModalStore();
  const { openPricingModal } = usePricingActions();
  const [menuOpen, setMenuOpen] = useState(false);

  // Billing history hook - get plan data
  const { data: billingData } = useBillingHistory();

  // Get plan name from billing data
  const getPlanName = () => {
    // If no billing data, show Free
    if (!billingData?.data?.pricingPlan) {
      return "Free";
    }

    // Get plan name from pricing plan
    const planName = billingData.data.pricingPlan.name;

    // If subscription exists and is active, show plan name
    const subscription = billingData.data.subscription;
    if (subscription && subscription.status === "active") {
      return planName || "Free";
    }

    // Check if plan is expired
    if (subscription && subscription.expiresAt) {
      const expiresAt = new Date(subscription.expiresAt);
      const now = new Date();

      if (expiresAt < now) {
        // Subscription expired, show Free
        return "Free";
      }
    }

    // Default to plan name or Free
    return planName || "Free";
  };

  const handleSettings = (e: React.MouseEvent) => {
    if (isModifiedClick(e)) return; // yeni sekmede açılıyor, sidebar kapatılmasın
    if (isMobile()) setSidebarOpen(false);
  };

  const handleBilling = (e: React.MouseEvent) => {
    if (isModifiedClick(e)) return;
    if (isMobile()) setSidebarOpen(false);
  };

  const handleLogout = () => {
    // Use modal system instead of window.confirm
    openLogout(() => {
      try {
        // mixpanel may not be initialized in some sessions.
        mixpanel.track("user_logged_out");
        mixpanel.reset();
        // `reset()` cookie'yi ve bütün super property'leri siliyor. `source`
        // hemen geri yazılmazsa çıkış ile bir sonraki giriş ARASINDAKİ event'ler
        // kanalsız gider; login sonrası `establish-session` zaten geri yazıyor
        // ama o araya düşenler için geç kalıyor.
        registerClientSource();
      } catch (error) {
        console.warn("Mixpanel logout tracking skipped:", error);
      }

      if (typeof window !== "undefined" && window.netmera?.push) {
        window.netmera.push((api) => {
          api.sendEvent({
            code: "nhanu",
          });
        });
      }
      // Clear auth state
      const authStore = useAuthStore.getState();
      authStore.logout();
      // Navigate to login page using React Router
      navigate("/login", { replace: true });
    });
  };

  const handlePersona = (e: React.MouseEvent) => {
    if (isModifiedClick(e)) return;
    if (isMobile()) setSidebarOpen(false);
  };

  const handleAppearance = (e: React.MouseEvent) => {
    if (isModifiedClick(e)) return;
    if (isMobile()) setSidebarOpen(false);
  };

  const handleHelp = (e: React.MouseEvent) => {
    // Yardım = dahili destek sayfası (SupportButton ile aynı hedef)
    if (isModifiedClick(e)) return;
    if (isMobile()) setSidebarOpen(false);
  };

  const handleUpgrade = () => {
    if (typeof window !== "undefined" && window.netmera?.push) {
      window.netmera.push((api) => {
        api.sendEvent({ code: "qcsfp" });
      });
    }
    try {
      mixpanel.track("upgrade_plan_clicked");
    } catch {
      // mixpanel may not be initialized
    }
    openPricingModal();
    setMenuOpen(false);
  };

  const planName = getPlanName();
  const isFreePlan = planName === "Free";

  // 🆕 Profile artık her zaman bir dropdown/popover: sidebar açık ya da kapalı
  // olsun, açılması "Chats" listesini kapatan bir accordion değil (bkz.
  // Conversations.tsx). Menü içeriği tek bir yerde tanımlanıyor, sadece
  // trigger görünümü ve açılma yönü (side/align) sidebar durumuna göre değişiyor.
  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
      <SidebarTooltip
        label={user?.name || t("user.profile")}
        disabled={isSidebarOpen}
      >
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              "w-full flex items-center rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none",
              isSidebarOpen
                ? "px-3 py-2 gap-3"
                : "px-2 py-[8px] justify-center"
            )}
          >
            {/* Avatar - Always visible */}
            <div className="relative w-8 h-8 flex items-center justify-center shrink-0 overflow-hidden rounded-full bg-bg-secondary dark:bg-gray-700 border border-border-primary dark:border-gray-600">
              {user?.image && getFileUrl(user.image) ? (
                <img
                  src={getFileUrl(user.image)!}
                  alt={user?.name || "User"}
                  className="size-full rounded-full object-cover"
                />
              ) : (
                <span className="text-text-quaternary dark:text-gray-300 font-semibold text-xs">
                  {(user?.name || "U").charAt(0).toUpperCase()}
                </span>
              )}
              {/* Ücretsiz plandaki kullanıcılar için küçük upgrade rozeti */}
              {isFreePlan && (
                <span className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 ring-2 ring-bg-primary dark:ring-gray-900">
                  <Crown className="w-2 h-2 text-white" />
                </span>
              )}
            </div>

            {isSidebarOpen && (
              <div className="flex items-center justify-between flex-1 min-w-0">
                <div className="flex flex-col items-start min-w-0 flex-1 text-left">
                  <p className="font-semibold text-text-sm text-[#414651] dark:text-gray-100 truncate w-full">
                    {user?.name || "User"}
                  </p>
                  <p className="font-normal text-text-xs text-text-quaternary dark:text-gray-400 truncate w-full">
                    {planName}
                  </p>
                </div>
                <div className="size-[16px] flex items-center justify-center shrink-0 p-1 rounded-[4px] bg-secondary">
                  <ChevronsUpDown size={16} className="text-[#a4a7ae]" />
                </div>
              </div>
            )}
          </button>
        </DropdownMenuTrigger>
      </SidebarTooltip>

      <DropdownMenuContent
        side={isSidebarOpen ? "top" : "right"}
        align={isSidebarOpen ? "start" : "end"}
        sideOffset={8}
        className="w-64 bg-white dark:bg-gray-800 border border-border-primary dark:border-gray-700 shadow-lg z-[1200]"
      >
        {/* User Info Header */}
        <div className="px-3 py-3 border-b border-border-secondary dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-bg-secondary dark:bg-gray-700 border border-border-primary dark:border-gray-600 flex items-center justify-center text-text-quaternary dark:text-gray-300 font-semibold shrink-0 overflow-hidden">
              {user?.image && getFileUrl(user.image) ? (
                <img
                  src={getFileUrl(user.image)!}
                  alt={user?.name || "User"}
                  className="size-full rounded-full object-cover"
                />
              ) : (
                <span>{(user?.name || "U").charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-text-primary dark:text-gray-100 truncate">
                {user?.name || "User"}
              </p>
              <p className="text-xs text-text-quaternary dark:text-gray-400 truncate">
                {user?.email || "user@example.com"}
              </p>
            </div>
          </div>
        </div>

        {/* Upgrade Plan Button - sadece Free plandaki kullanıcılara göster */}
        {isFreePlan && (
          <div className="px-2 py-2">
            <FancyUpgradeButton
              onClick={handleUpgrade}
              label={t("user.upgradePlan")}
            />
          </div>
        )}

        <DropdownMenuSeparator className="bg-border-secondary dark:bg-gray-700" />

        {/* Menu Items */}
        <DropdownMenuItem
          asChild
          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 text-text-primary dark:text-gray-100"
        >
          <Link to="/settings/profile" onClick={handlePersona}>
            <UserRound className="w-4 h-4" />
            {t("user.persona")}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem
          asChild
          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 text-text-primary dark:text-gray-100"
        >
          <Link to="/settings/appearance" onClick={handleAppearance}>
            <Sparkles className="w-4 h-4" />
            {t("settings.tabs.appearance")}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem
          asChild
          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 text-text-primary dark:text-gray-100"
        >
          <Link to="/settings/profile" onClick={handleSettings}>
            <Settings className="w-4 h-4" />
            {t("user.settings")}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem
          asChild
          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 text-text-primary dark:text-gray-100"
        >
          <Link to="/settings/billing" onClick={handleBilling}>
            <Crown className="w-4 h-4" />
            {t("user.billing")}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem
          asChild
          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 text-text-primary dark:text-gray-100"
        >
          <Link to="/support" onClick={handleHelp}>
            <HelpCircle className="w-4 h-4" />
            {t("common.help")}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-border-secondary dark:bg-gray-700" />

        <DropdownMenuItem
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 text-destructive dark:text-red-400"
        >
          <LogOut className="w-4 h-4" />
          {t("user.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
